/**
 * Pipeline de um fit aprovado (equipe): etapa, conversa com o cliente, anexos e histórico.
 * Endpoints em /admin/fits/:fitId/...; toda mutação invalida a fila, a ficha do edital (contadores e etapa) e o painel.
 */
import type { Anexo, Etapa, Evento } from '@licita/contracts';
import { ANEXO_MAX_BYTES, ANEXO_TIPOS, ETAPAS_FINAIS } from '@licita/contracts';
import { queryOptions } from '@tanstack/react-query';
import { api, apiUpload } from './api';
import { queryClient } from './queries';

type Tone = 'ok' | 'warn' | 'bad' | 'info' | 'muted';

const base = (fitId: string) => `/admin/fits/${fitId}`;

export const eventosQuery = (fitId: string) =>
  queryOptions({
    queryKey: ['pipeline', fitId, 'eventos'],
    queryFn: () => api<{ itens: Evento[] }>(`${base(fitId)}/eventos`),
    select: (r) => [...r.itens].sort((a, b) => a.em.localeCompare(b.em)),
  });

export const anexosQuery = (fitId: string) =>
  queryOptions({
    queryKey: ['pipeline', fitId, 'anexos'],
    queryFn: () => api<{ itens: Anexo[] }>(`${base(fitId)}/anexos`),
    select: (r) => [...r.itens].sort((a, b) => b.em.localeCompare(a.em)),
  });

/** Fila de triagem e quadro (`fits`), ficha do edital (`edital`, traz os fits com a etapa) e o painel do fit. */
const invalidar = (fitId: string) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: ['fits'] }),
    queryClient.invalidateQueries({ queryKey: ['edital'] }),
    queryClient.invalidateQueries({ queryKey: ['pipeline', fitId] }),
  ]);

export async function mudarEtapa(fitId: string, etapa: Etapa, motivo: string | null): Promise<void> {
  await api<{ ok: true }>(`${base(fitId)}/etapa`, { method: 'POST', json: { etapa, motivo } });
  await invalidar(fitId);
}

export async function comentar(fitId: string, texto: string): Promise<void> {
  await api<{ ok: true }>(`${base(fitId)}/comentarios`, { method: 'POST', json: { texto } });
  await invalidar(fitId);
}

export async function enviarAnexo(fitId: string, arquivo: File): Promise<string> {
  const { id } = await apiUpload<{ id: string }>(`${base(fitId)}/anexos`, arquivo);
  await invalidar(fitId);
  return id;
}

export async function removerAnexo(fitId: string, anexoId: string): Promise<void> {
  await api<{ ok: true }>(`${base(fitId)}/anexos/${anexoId}`, { method: 'DELETE' });
  await invalidar(fitId);
}

export const linkAnexo = (fitId: string, anexoId: string) => api<{ url: string }>(`${base(fitId)}/anexos/${anexoId}/link`);

// ---------- rótulos e validação ----------

export const etapaFinal = (e: Etapa) => ETAPAS_FINAIS.includes(e);

export const ETAPA_TONE: Record<Etapa, Tone> = {
  recebido: 'muted',
  interessado: 'info',
  preparando: 'info',
  em_disputa: 'warn',
  aguardando_resultado: 'warn',
  venceu: 'ok',
  perdeu: 'bad',
  desistiu: 'muted',
  suspenso: 'muted',
};

/** Rótulo do campo "motivo" pedido nas etapas finais. */
export const MOTIVO_ROTULO: Partial<Record<Etapa, string>> = {
  venceu: 'Valor vencedor e colocação',
  perdeu: 'Valor vencedor e colocação',
  desistiu: 'Motivo da desistência',
  suspenso: 'Motivo',
};

export const ANEXO_ACCEPT = Object.keys(ANEXO_TIPOS).join(',');
export const ANEXO_FORMATOS = Object.keys(ANEXO_TIPOS)
  .map((e) => e.slice(1).toUpperCase())
  .join(', ');
export const ANEXO_MAX_MB = Math.round(ANEXO_MAX_BYTES / 1024 / 1024);

/** Checagem local antes do upload (a API repete a validação). Devolve a mensagem de erro ou null. */
export function validarArquivo(f: File): string | null {
  const ext = f.name.includes('.') ? `.${f.name.split('.').pop()!.toLowerCase()}` : '';
  if (!ext || !(ext in ANEXO_TIPOS)) return `"${f.name}": formato não aceito. Use ${ANEXO_FORMATOS}.`;
  if (f.size === 0) return `"${f.name}": o arquivo está vazio.`;
  if (f.size > ANEXO_MAX_BYTES) return `"${f.name}": passa de ${ANEXO_MAX_MB} MB.`;
  return null;
}

/** "3 comentários · 1 anexo" */
export const contagens = (p: { comentarios: number; anexos: number }) =>
  `${p.comentarios} ${p.comentarios === 1 ? 'comentário' : 'comentários'} · ${p.anexos} ${p.anexos === 1 ? 'anexo' : 'anexos'}`;

/** Abas do painel do fit; a ativa fica na URL (`/fits/:id?aba=conversa`). */
export const ABAS_PAINEL = { dados: 'Dados', anexos: 'Anexos', conversa: 'Conversa', historico: 'Histórico' } as const;
export type AbaPainel = keyof typeof ABAS_PAINEL;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Busca do painel: aba ativa e o edital (para a ficha; não há endpoint unitário de fit). */
export const validarBuscaPainel = (raw: Record<string, unknown>): { aba: AbaPainel; edital?: string } => ({
  aba: typeof raw.aba === 'string' && raw.aba in ABAS_PAINEL ? (raw.aba as AbaPainel) : 'dados',
  edital: typeof raw.edital === 'string' && UUID.test(raw.edital) ? raw.edital : undefined,
});
